import type { PasswordResetResult, SignInResult, SignInStep } from "@/lib/auth/sign-in/result";

export class SignInRequestError extends Error {
  constructor(
    readonly step: SignInStep,
    readonly status: number,
  ) {
    super(`Sign-in step ${step} answered ${status}`);
    this.name = "SignInRequestError";
  }
}

async function postStep<Result>(step: SignInStep, input: object): Promise<Result> {
  const response = await fetch(`/api/auth/sign-in/${step}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) throw new SignInRequestError(step, response.status);
  return response.json();
}

export function signInWithPassword(input: {
  email: string;
  password: string;
  quoteClaimToken?: string;
}): Promise<SignInResult> {
  return postStep("password", input);
}

export function signUpWithPassword(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  quoteClaimToken?: string;
}): Promise<SignInResult> {
  return postStep("sign-up", input);
}

export function signInWithEmailCode(input: {
  email: string;
  code: string;
  quoteClaimToken?: string;
  listingRequest?: unknown;
}): Promise<SignInResult> {
  return postStep("email-code", input);
}

export function createQuoteAccount(input: {
  token: string;
  code: string;
  password: string;
}): Promise<SignInResult> {
  return postStep("quote-account", input);
}

export function confirmPendingSignIn(input: { code: string }): Promise<SignInResult> {
  return postStep("pending-code", input);
}

export function sendPasswordReset(input: { email: string }): Promise<PasswordResetResult> {
  return postStep("password-reset", input);
}

export async function hasSignedInSession(): Promise<boolean> {
  const response = await fetch("/api/auth/access-token", { cache: "no-store", credentials: "same-origin" });
  if (response.ok) return true;
  if (response.status === 401) return false;
  throw new Error(`Session check answered ${response.status}`);
}
